import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  PortalLoading,
  TeacherPageHeader,
  useTeacherPortal,
} from "./teacherPortalUtils";

const emptyForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  phone: "",
  email: "",
  address: "",
  current_class_group: "",
  enrollment_date: "",
  previous_school: "",
  medical_conditions: "",
  allergies: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
};

function TeacherStudentsPage() {
  const { data, loading, reload } = useTeacherPortal("teacher-portal/students/");

  const [search, setSearch] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [credential, setCredential] = useState(null);

  const groups = data?.class_teacher_groups || [];
  const students = data?.students || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return students;

    return students.filter((student) =>
      [
        student.full_name,
        student.student_number,
        student.class_group_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [students, search]);

  if (loading && !data) {
    return <PortalLoading label="Loading class students..." />;
  }

  if (data?.can_manage === false) {
    return (
      <div className="container-fluid py-4">
        <TeacherPageHeader
          title="My Students"
          subtitle="Student registration and editing is available to designated class teachers."
          icon="bi-people"
        />
        <div className="card border-0 shadow-sm">
          <EmptyState
            title="Class teacher access required"
            text={data?.message || "This module is available to class teachers only."}
          />
        </div>
      </div>
    );
  }

  const openRegister = () => {
    setCredential(null);

    setForm({
      ...emptyForm,
      current_class_group:
        groups.length === 1 ? String(groups[0].id) : "",
    });

    setEditing(null);
    setShowRegister(true);
  };

  const openEdit = (student) => {
    setCredential(null);
    setShowRegister(false);
    setEditing(student);

    setForm({
      first_name: student.first_name || "",
      middle_name: student.middle_name || "",
      last_name: student.last_name || "",
      gender: student.gender || "",
      date_of_birth: student.date_of_birth || "",
      phone: student.phone || "",
      email: student.email || "",
      address: student.address || "",
      current_class_group: student.current_class_group
        ? String(student.current_class_group)
        : "",
      enrollment_date: student.enrollment_date || "",
      previous_school: student.previous_school || "",
      medical_conditions: student.medical_conditions || "",
      allergies: student.allergies || "",
      emergency_contact_name: student.emergency_contact_name || "",
      emergency_contact_phone: student.emergency_contact_phone || "",
    });
  };

  const setField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveRegistration = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const response = await axiosInstance.post(
        "teacher-portal/students/",
        form
      );

      setCredential({
        student_name: response.data?.student?.full_name,
        student_number: response.data?.student?.student_number,
        temporary_password: response.data?.student?.temporary_password,
      });

      toast.success("Student registered successfully.");

      setShowRegister(false);
      setForm(emptyForm);
      reload();
    } catch (error) {
      const detail = error.response?.data;

      toast.error(
        detail?.detail ||
          Object.values(detail || {}).flat().join(" ") ||
          "Failed to register student."
      );
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      await axiosInstance.patch(
        `teacher-portal/students/${editing.id}/`,
        form
      );

      toast.success("Student details updated.");
      setEditing(null);
      setForm(emptyForm);
      reload();
    } catch (error) {
      const detail = error.response?.data;

      toast.error(
        detail?.detail ||
          Object.values(detail || {}).flat().join(" ") ||
          "Failed to update student."
      );
    } finally {
      setSaving(false);
    }
  };

  const removeStudent = async (student) => {
    if (!window.confirm(`Remove ${student.full_name} from your class? The student's historical records will be preserved.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`teacher-portal/students/${student.id}/`);
      toast.success("Student removed from your class. Historical records were preserved.");
      if (editing?.id === student.id) setEditing(null);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove student.");
    }
  };

  const resetPassword = async (student) => {
    if (
      !window.confirm(
        `Reset password for ${student.full_name}?`
      )
    ) {
      return;
    }

    try {
      const response = await axiosInstance.post(
        `teacher-portal/students/${student.id}/reset-password/`
      );

      setCredential({
        student_name: response.data.student_name,
        student_number: response.data.student_number,
        temporary_password: response.data.temporary_password,
      });

      toast.success("Student password reset.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Password reset failed."
      );
    }
  };

  const StudentForm = ({ title, onSubmit }) => (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h6 className="fw-bold mb-0">{title}</h6>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => {
            setShowRegister(false);
            setEditing(null);
          }}
        >
          Cancel
        </button>
      </div>

      <div className="card-body">
        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-md-4">
            <label className="form-label">First Name *</label>
            <input
              className="form-control"
              value={form.first_name}
              onChange={(e) => setField("first_name", e.target.value)}
              required
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Middle Name</label>
            <input
              className="form-control"
              value={form.middle_name}
              onChange={(e) => setField("middle_name", e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Last Name *</label>
            <input
              className="form-control"
              value={form.last_name}
              onChange={(e) => setField("last_name", e.target.value)}
              required
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Gender</label>
            <select
              className="form-select"
              value={form.gender}
              onChange={(e) => setField("gender", e.target.value)}
            >
              <option value="">Select</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              className="form-control"
              value={form.date_of_birth}
              onChange={(e) => setField("date_of_birth", e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Phone</label>
            <input
              className="form-control"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Class *</label>
            <select
              className="form-select"
              value={form.current_class_group}
              onChange={(e) =>
                setField("current_class_group", e.target.value)
              }
              required
            >
              <option value="">Select class</option>

              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Enrollment Date</label>
            <input
              type="date"
              className="form-control"
              value={form.enrollment_date}
              onChange={(e) =>
                setField("enrollment_date", e.target.value)
              }
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Previous School</label>
            <input
              className="form-control"
              value={form.previous_school}
              onChange={(e) =>
                setField("previous_school", e.target.value)
              }
            />
          </div>

          <div className="col-12">
            <label className="form-label">Address</label>
            <textarea
              className="form-control"
              rows="2"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Medical Conditions</label>
            <input
              className="form-control"
              value={form.medical_conditions}
              onChange={(e) =>
                setField("medical_conditions", e.target.value)
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Allergies</label>
            <input
              className="form-control"
              value={form.allergies}
              onChange={(e) => setField("allergies", e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Emergency Contact Name</label>
            <input
              className="form-control"
              value={form.emergency_contact_name}
              onChange={(e) =>
                setField("emergency_contact_name", e.target.value)
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Emergency Contact Phone</label>
            <input
              className="form-control"
              value={form.emergency_contact_phone}
              onChange={(e) =>
                setField("emergency_contact_phone", e.target.value)
              }
            />
          </div>

          <div className="col-12">
            <button
              className="btn btn-success"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : title}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <TeacherPageHeader
        title="My Students"
        subtitle="Register and maintain students in your class."
        icon="bi-people"
        actions={
          <button className="btn btn-success" onClick={openRegister}>
            <i className="bi bi-person-plus me-2"></i>
            Register Student
          </button>
        }
      />

      {credential && (
        <div className="alert alert-success shadow-sm">
          <div className="fw-bold mb-1">
            Student Login Credentials
          </div>

          <div>
            <strong>Student:</strong> {credential.student_name}
          </div>

          <div>
            <strong>Student Number:</strong>{" "}
            {credential.student_number}
          </div>

          <div>
            <strong>Temporary Password:</strong>{" "}
            <span className="font-monospace">
              {credential.temporary_password}
            </span>
          </div>

          <small>
            Record these credentials before leaving this page.
          </small>
        </div>
      )}

      {showRegister && (
        <StudentForm
          title="Register Student"
          onSubmit={saveRegistration}
        />
      )}

      {editing && (
        <StudentForm
          title="Update Student"
          onSubmit={saveEdit}
        />
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <div className="row g-2 align-items-center">
            <div className="col-md-7">
              <h6 className="fw-bold mb-0">My Class Students</h6>
            </div>

            <div className="col-md-5">
              <input
                className="form-control"
                placeholder="Search name or student number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Student Number</th>
                <th>Student Name</th>
                <th>Class</th>
                <th>Gender</th>
                <th style={{ width: 270 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((student) => (
                <tr key={student.id}>
                  <td className="fw-semibold">
                    {student.student_number}
                  </td>

                  <td>{student.full_name}</td>
                  <td>{student.class_group_name || "-"}</td>
                  <td>{student.gender || "-"}</td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEdit(student)}
                      >
                        Edit
                      </button>

                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => resetPassword(student)}
                      >
                        Reset Password
                      </button>


                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeStudent(student)}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    No students found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TeacherStudentsPage;
